import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { sprekersApi } from '../api';
import { persistLiveSession } from '../liveSessionStorage';

/**
 * Landingspagina voor de QR-koppeling (sectie 16.5): dit is de URL die in de
 * QR-code op het beamerscherm staat. Koppelt de telefoon van de spreker aan
 * de sessie en stuurt daarna door naar de bedieningspagina.
 */
export default function SessionPairPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryToken = searchParams.get('token') || '';

  const [tokenInput, setTokenInput] = useState(queryToken);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoAttempted = useRef(false);

  const connect = async (tokenToUse: string) => {
    if (!sessionId || !tokenToUse.trim()) return;
    setConnecting(true);
    setError(null);
    try {
      const session = await sprekersApi.connectSession(sessionId, tokenToUse.trim());
      persistLiveSession(session);
      navigate(`/sprekers/sessies/${sessionId}/bedienen`, { replace: true, state: { session } });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Koppelen mislukt');
    } finally {
      setConnecting(false);
    }
  };

  useEffect(() => {
    if (queryToken && !autoAttempted.current) {
      autoAttempted.current = true;
      // Het token is een bedieningsgeheim -- direct uit de adresbalk/geschiedenis
      // verwijderen zodra het gelezen is.
      window.history.replaceState(null, '', `/sprekers/sessies/${sessionId}/koppelen`);
      connect(queryToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryToken]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    connect(tokenInput);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-950 p-6 text-white">
      <h1 className="text-xl font-semibold">Koppelen aan live sessie</h1>
      {connecting && <p className="text-slate-300">Bezig met koppelen...</p>}
      {error && <p className="max-w-sm text-center text-red-400">{error}</p>}
      {!connecting && (
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-2">
          <Label htmlFor="pair-token" className="text-slate-300">Koppelcode</Label>
          <Input
            id="pair-token" value={tokenInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTokenInput(e.target.value)}
            className="bg-white text-slate-900" placeholder="Koppelcode" autoComplete="off"
          />
          <Button type="submit" className="w-full" disabled={!tokenInput.trim()}>Koppelen</Button>
        </form>
      )}
    </div>
  );
}
